import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
	type NotificationTemplateDTO,
	OfferErrorCode,
	OfferNotificationTemplateChannel,
	OfferStatus,
	ValidationErrorCode,
} from '@today/types'
import type { MessagePickerOption, MessagePreviewView } from '@today/ui-domain'
import { useEffect, useRef, useState } from 'react'
import { AUTOSAVE_DELAY_MS } from '@/modules/offer-wizard/useOfferMessageStep'
import { notificationTemplateService } from '@/services/notification-template.service'
import { type OfferDTO, type OfferMessagePatch, offerService } from '@/services/offer.service'
import { apiErrorOf, templatePreviewErrorOf } from './api-error'
import { MESSAGE_TEMPLATE_VARIABLES, NOTIFICATION_BODY_MAX } from './message-variables'
import { MessageChoice, pickerOptions } from './picker-options'
import { preselectTemplate } from './preselect-template'

const SAVING = 'Saving…'
const SAVED = 'All changes saved'

/** The save indicator's message for a refused message save (TO-140 PATCH contract). */
export const messageSaveErrorOf = (error: unknown): string => {
	switch (apiErrorOf(error).code) {
		case ValidationErrorCode.OFFER_NOTIFICATION_BODY_INVALID:
			return 'The message is not valid. Check its length and its {{ … }} variables.'
		case ValidationErrorCode.NOTIFICATION_TEMPLATE_UNAVAILABLE:
			return 'This template is no longer available. Pick another one.'
		case OfferErrorCode.OFFER_NOT_EDITABLE:
			return 'This offer has already been sent and can no longer be edited.'
		default:
			return 'The message could not be saved. Try again.'
	}
}

/** Blank editor text is no message. */
const bodyOf = (text: string): string | null => (text.trim() ? text : null)

/** The rendered message for the preview pane, showing only the latest response. */
function useMessagePreview(offerId: string) {
	const [preview, setPreview] = useState<MessagePreviewView | null>(null)
	const [error, setError] = useState<string | null>(null)
	const seqRef = useRef(0)
	const run = (subject: string | null, body: string) => {
		const seq = ++seqRef.current
		if (!body.trim()) {
			setPreview(null)
			setError(null)
			return
		}
		notificationTemplateService
			.preview({ offerId, subject, body })
			.then((rendered) => {
				if (seq !== seqRef.current) return
				setPreview(rendered)
				setError(null)
			})
			.catch((refusal: unknown) => {
				if (seq === seqRef.current) setError(templatePreviewErrorOf(refusal))
			})
	}
	return { preview, error, run }
}

/** PATCHes the message fields, keeping the cached offer and a status in step. */
function useMessageSave(offerId: string) {
	const client = useQueryClient()
	const [status, setStatus] = useState<string | null>(null)
	const save = (patch: OfferMessagePatch) => {
		setStatus(SAVING)
		offerService
			.update(offerId, patch)
			.then((updated) => {
				client.setQueryData(['offer', offerId], updated)
				setStatus(SAVED)
			})
			.catch((refusal: unknown) => setStatus(messageSaveErrorOf(refusal)))
	}
	return { status, save }
}

/** One restartable timer, cleared on unmount. */
function useDebounce() {
	const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
	useEffect(() => () => clearTimeout(timer.current), [])
	return {
		schedule: (run: () => void, ms: number) => {
			clearTimeout(timer.current)
			timer.current = setTimeout(run, ms)
		},
		cancel: () => clearTimeout(timer.current),
	}
}

const subjectOf = (template: NotificationTemplateDTO | undefined): string | null =>
	template?.channelType === OfferNotificationTemplateChannel.EMAIL ? template.subject : null

/** The panel's editing state: the picker choice, the editor text, the preview, the save and the autosave timer. */
function useMessageState(offerId: string) {
	const preview = useMessagePreview(offerId)
	const saver = useMessageSave(offerId)
	const debounce = useDebounce()
	const [choice, setChoice] = useState<string>(MessageChoice.WRITE_CUSTOM)
	const [body, setBody] = useState('')
	const showTemplate = (template: NotificationTemplateDTO) => {
		setChoice(template.id)
		setBody(template.body)
		preview.run(subjectOf(template), template.body)
	}
	return { preview, saver, debounce, choice, setChoice, body, setBody, showTemplate }
}

type MessageState = ReturnType<typeof useMessageState>

/**
 * The first state of the panel: the stored link or body as it is, else the
 * language default (linked at once, unless read-only), else Write custom.
 */
function seedFrom(
	offer: OfferDTO,
	library: readonly NotificationTemplateDTO[],
	readOnly: boolean,
	state: MessageState,
) {
	const stored = {
		locale: offer.locale,
		notificationTemplateId: offer.notificationTemplateId ?? null,
		notificationBody: offer.notificationBody ?? null,
	}
	if (stored.notificationTemplateId !== null) {
		state.setChoice(stored.notificationTemplateId)
		const linked = library.find((t) => t.id === stored.notificationTemplateId)
		if (linked) state.showTemplate(linked)
		return
	}
	if (stored.notificationBody !== null) {
		state.setChoice(MessageChoice.CUSTOM)
		state.setBody(stored.notificationBody)
		state.preview.run(null, stored.notificationBody)
		return
	}
	const preselected = readOnly ? null : preselectTemplate(library, stored)
	if (!preselected) return
	state.showTemplate(preselected)
	state.saver.save({ notificationTemplateId: preselected.id })
}

/** Picker selection and editor edits; both ignored on a read-only offer. */
function actionsOf(state: MessageState, library: readonly NotificationTemplateDTO[], readOnly: boolean) {
	const select = (value: string) => {
		if (readOnly || value === MessageChoice.CUSTOM) return
		state.debounce.cancel()
		if (value === MessageChoice.WRITE_CUSTOM) {
			state.setChoice(value)
			state.setBody('')
			state.preview.run(null, '')
			state.saver.save({ notificationTemplateId: null, notificationBody: null })
			return
		}
		const template = library.find((t) => t.id === value)
		if (!template) return
		state.showTemplate(template)
		state.saver.save({ notificationTemplateId: template.id })
	}
	const edit = (text: string) => {
		if (readOnly) return
		state.setChoice(MessageChoice.CUSTOM)
		state.setBody(text)
		state.debounce.schedule(() => {
			state.saver.save({ notificationBody: bodyOf(text) })
			state.preview.run(null, text)
		}, AUTOSAVE_DELAY_MS)
	}
	return { select, edit }
}

/**
 * Offer Wizard Step 3's notification message (TO-140 offer-notification-message
 * REQ-106..REQ-108): the picker of the offer language's active templates, the
 * language default preselected (and linked) for an offer with no message, a
 * template's source shown on selection with its rendered preview, an edit saved
 * as the custom body {@link AUTOSAVE_DELAY_MS} after the last keystroke, and
 * "Write custom" clearing both fields. A non-DRAFT offer is read-only.
 */
export function useOfferNotificationMessage(offerId: string, locale: string) {
	const offer = useQuery<OfferDTO>({ queryKey: ['offer', offerId], queryFn: () => offerService.get(offerId) })
	const templates = useQuery<NotificationTemplateDTO[]>({
		queryKey: ['offer-notification', 'templates', 'active'],
		queryFn: () => notificationTemplateService.list({ isActive: true }),
		retry: false,
	})
	const state = useMessageState(offerId)
	const seededRef = useRef(false)
	const readOnly = offer.data !== undefined && offer.data.status !== OfferStatus.DRAFT
	const library = templates.data ?? []

	// Seed once, after the library settled (or failed).
	// biome-ignore lint/correctness/useExhaustiveDependencies: seeds once per loaded offer + library; later refetches must not discard edits
	useEffect(() => {
		if (!offer.data || templates.isPending || seededRef.current) return
		seededRef.current = true
		seedFrom(offer.data, library, readOnly, state)
	}, [offer.data, templates.isPending])

	const { select, edit } = actionsOf(state, library, readOnly)
	const options: MessagePickerOption[] = pickerOptions(library, locale)
	if (state.choice === MessageChoice.CUSTOM) options.unshift({ value: MessageChoice.CUSTOM, label: 'Custom' })
	return {
		options,
		selected: state.choice,
		body: state.body,
		maxLength: NOTIFICATION_BODY_MAX,
		subject: subjectOf(library.find((t) => t.id === state.choice)),
		preview: state.preview.preview,
		previewError: state.preview.error,
		variables: MESSAGE_TEMPLATE_VARIABLES,
		readOnly,
		saveStatus: state.saver.status,
		onSelect: select,
		onBodyChange: edit,
	}
}
